/**
 * Rechnet Messreihen in Linienzuege fuer das Diagramm um (W12).
 *
 * Getrennt von der Darstellung, weil hier die Entscheidungen stecken, die man
 * pruefen will: Achsenbereich, Umgang mit Luecken und der Fall einer Reihe
 * ohne Schwankung.
 */

import type { Channel } from "./series";

export interface Bounds {
  min: number;
  max: number;
}

/**
 * Bestimmt den Wertebereich einer Reihe.
 *
 * Luecken zaehlen nicht mit. Eine Reihe ohne Schwankung bekommt einen kleinen
 * Bereich um ihren Wert, sonst waere die Hoehe null und die Linie unsichtbar.
 */
export function bounds(values: (number | null)[]): Bounds | undefined {
  let min = Infinity;
  let max = -Infinity;
  for (const value of values) {
    if (value === null) {
      continue;
    }
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }
  if (min === Infinity) {
    return undefined;
  }
  if (min === max) {
    const padding = Math.abs(min) > 1 ? Math.abs(min) * 0.1 : 0.5;
    return { min: min - padding, max: max + padding };
  }
  // Etwas Luft, damit die Linie nicht am Rand klebt.
  const margin = (max - min) * 0.08;
  return { min: min - margin, max: max + margin };
}

export interface PlotArea {
  width: number;
  height: number;
}

/**
 * Baut die Linienzuege einer Reihe.
 *
 * Rueckgabe ist eine Liste von Teilstuecken. Jede Luecke beendet ein Stueck:
 * eine durchgezogene Linie ueber eine fehlende Messung hinweg wuerde einen
 * Verlauf behaupten, den niemand gemessen hat (G08).
 */
export function polylines(
  channel: Channel,
  times: number[],
  area: PlotArea,
  range?: Bounds
): string[] {
  const limits = range ?? bounds(channel.values);
  if (!limits || times.length === 0) {
    return [];
  }
  const first = times[0];
  const span = times[times.length - 1] - first;
  const height = limits.max - limits.min;

  const segments: string[] = [];
  let current: string[] = [];
  for (let index = 0; index < times.length; index += 1) {
    const value = channel.values[index];
    if (value === null || value === undefined) {
      if (current.length > 1) {
        segments.push(current.join(" "));
      }
      current = [];
      continue;
    }
    const x = span === 0 ? 0 : ((times[index] - first) / span) * area.width;
    const y = area.height - ((value - limits.min) / height) * area.height;
    current.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  if (current.length > 1) {
    segments.push(current.join(" "));
  }
  return segments;
}

/**
 * Waehlt Beschriftungen der Werteachse.
 *
 * Bewusst wenige: das Diagramm steht in einer schmalen Spalte, und eine dicht
 * beschriftete Achse ist dort schlechter lesbar als eine grobe.
 */
export function ticks(range: Bounds, count = 3): number[] {
  const result: number[] = [];
  for (let index = 0; index < count; index += 1) {
    result.push(range.min + ((range.max - range.min) * index) / (count - 1));
  }
  return result;
}

/** Formatiert einen Messwert kurz und ohne irrefuehrende Genauigkeit. */
export function formatValue(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude === 0) {
    return "0";
  }
  if (magnitude < 0.01 || magnitude >= 10000) {
    return value.toExponential(1);
  }
  if (magnitude < 1) {
    return value.toFixed(3);
  }
  if (magnitude < 100) {
    return value.toFixed(2);
  }
  return value.toFixed(0);
}

/**
 * Farben der Messreihen.
 *
 * Ausgewaehlt, damit sie sich auch bei Rot-Gruen-Schwaeche unterscheiden
 * lassen: die Reihenfolge wechselt zwischen kalten und warmen Toenen und
 * zwischen hell und dunkel.
 */
export const SERIES_COLOURS = [
  "#0ea5e9",
  "#f97316",
  "#16a34a",
  "#a855f7",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#64748b",
];

export function colourFor(index: number): string {
  return SERIES_COLOURS[index % SERIES_COLOURS.length];
}

/**
 * Gemeinsamer Wertebereich mehrerer Reihen.
 *
 * Mehrere Linien in einem Diagramm brauchen eine gemeinsame Achse, sonst
 * liessen sich die Kurven nicht miteinander vergleichen - und genau dafuer
 * legt man sie uebereinander.
 */
export function combinedBounds(channels: Channel[]): Bounds | undefined {
  let min = Infinity;
  let max = -Infinity;
  for (const channel of channels) {
    const range = bounds(channel.values);
    if (!range) {
      continue;
    }
    if (range.min < min) {
      min = range.min;
    }
    if (range.max > max) {
      max = range.max;
    }
  }
  if (min === Infinity) {
    return undefined;
  }
  return { min, max };
}

/**
 * Waehlt runde Werte fuer die Achsenbeschriftung.
 *
 * Krumme Zahlen an der Achse sind schwer zu lesen. Professionelle Diagramme
 * nehmen deshalb Vielfache von 1, 2 oder 5 mal einer Zehnerpotenz.
 */
export function niceTicks(range: Bounds, wunsch = 4): number[] {
  const spanne = range.max - range.min;
  if (spanne <= 0) {
    return [range.min];
  }
  const roh = spanne / wunsch;
  const groesse = Math.pow(10, Math.floor(Math.log10(roh)));
  const normiert = roh / groesse;
  const schritt =
    (normiert <= 1 ? 1 : normiert <= 2 ? 2 : normiert <= 5 ? 5 : 10) * groesse;
  const werte: number[] = [];
  const start = Math.ceil(range.min / schritt) * schritt;
  for (let wert = start; wert <= range.max + schritt * 1e-9; wert += schritt) {
    // Rundungsfehler der Schleife wegputzen, sonst steht dort 0.30000000004.
    werte.push(Number(wert.toPrecision(12)));
  }
  return werte;
}
